import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { ArrowRight, Building2, Globe as Globe2, BrainCircuit, Palette, BarChart3, Megaphone, Laptop, ExternalLink, Tag, CheckCircle2 } from 'lucide-react';
import { partnersData } from '../partnersData';
import { Client } from '../clientsData';
import ClientCard from '../components/ClientCard';
import ServiceCard from '../components/ServiceCard';
import StarModelCard from '../components/StarModelCard';
import AnimatedSection from '../components/AnimatedSection';
import TestimonialCard from '../components/TestimonialCard';
import SEO from '../components/SEO';
import PortfolioCard from '../components/PortfolioCard';
import { imageAssets } from '../content/imageAssets';
import { scrollToSection } from '../utils/scroll';
import SectionHeading from '../components/SectionHeading';
import StarSystem from '../components/StarSystem';
import { useSpotlight } from '../hooks/useSpotlight';
import { useParallaxInput } from '../hooks/useParallaxInput';
import HeroFluid from '../components/HeroFluid';
import { useI18n } from '../i18n/I18nContext';

interface HomePageProps {
  visibleClients: Client[];
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const RECAPTCHA_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const encode = (data: Record<string, string>) =>
  Object.entries(data)
    .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v))
    .join('&');

function PartnerTeaserCard({ partner }: { partner: typeof partnersData[0] }) {
  const { t } = useI18n();
  const [imgError, setImgError] = useState(false);
  const handleSpotlight = useSpotlight<HTMLDivElement>();

  const logo = partner.logoUrl && !imgError ? (
    <img
      src={partner.logoUrl}
      alt={`${partner.name} logo`}
      className="max-h-8 max-w-full object-contain"
      onError={() => setImgError(true)}
    />
  ) : partner.logoWordmark ? (
    <span className="font-bold text-base tracking-tight leading-none select-none">
      {partner.logoWordmark.text.split('').map((ch, i) =>
        ch === ' ' ? (
          <span key={i}>&nbsp;</span>
        ) : (
          <span key={i} style={{ color: partner.logoWordmark!.colors[i % partner.logoWordmark!.colors.length] }}>
            {ch}
          </span>
        )
      )}
    </span>
  ) : (
    <span className="font-bold text-white">{partner.name}</span>
  );

  return (
    <div onPointerMove={handleSpotlight} className="spotlight-card bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex flex-col gap-4 hover:border-[#FDCB12]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <div className="flex items-center justify-between gap-3">
        <div className="h-8 w-36 flex items-center flex-shrink-0">{logo}</div>
        <span className="text-xs font-medium text-[#FDCB12] bg-[#FDCB12]/10 px-2 py-0.5 rounded-full flex-shrink-0">{partner.category}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white mb-1">{partner.name}</p>
        <p className="text-gray-400 text-sm line-clamp-2">{partner.description}</p>
      </div>
      {partner.deal && (
        <div className="flex items-center gap-2 bg-[#FDCB12]/10 border border-[#FDCB12]/30 rounded-lg px-3 py-2">
          <Tag className="h-3.5 w-3.5 text-[#FDCB12] flex-shrink-0" />
          <span className="text-[#FDCB12] text-xs font-semibold">{partner.deal}</span>
        </div>
      )}
      <a
        href={partner.referralUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-1.5 bg-[#FDCB12] text-black text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300"
      >
        {t.partners.getStarted} <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

function HomePage({ visibleClients }: HomePageProps) {
  const { t, localizedPath } = useI18n();
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const heroRef = useParallaxInput<HTMLElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (RECAPTCHA_KEY && !captchaValue) {
      setFormStatus('error');
      setErrorMessage(t.contact.recaptchaError);
      return;
    }

    setFormStatus('submitting');
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const payload: Record<string, string> = {
      'form-name': 'main-form',
      'bot-field': '',
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      reason: formData.get('reason') as string,
      message: formData.get('message') as string,
    };

    if (captchaValue) {
      payload['g-recaptcha-response'] = captchaValue;
    }

    try {
      const response = await fetch('/index.html', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(payload),
      });

      if (!response.ok) throw new Error('Network response was not ok');
      setFormStatus('success');
      form.reset();
      setCaptchaValue(null);
      recaptchaRef.current?.reset();
    } catch {
      setFormStatus('error');
      setErrorMessage(t.contact.submitError);
      recaptchaRef.current?.reset();
      setCaptchaValue(null);
    }
  };

  const portfolioItems = [
    { title: 'Snouts', category: t.portfolio.catBrandWebsite, descKey: 'snouts' as const, image: imageAssets.portfolio.snouts, link: 'https://snouts.dog', projectType: 'showcase' as const, animationDirection: 'left' as const },
    { title: 'Innova Pro', category: t.portfolio.catWebDevelopment, descKey: 'innovaPro' as const, image: imageAssets.portfolio.innovaPro, link: 'https://innova-pro.ca', projectType: 'real-world' as const, animationDirection: 'right' as const },
    { title: 'Pawstuffs', category: t.portfolio.catEcommerceWebsite, descKey: 'pawstuffs' as const, image: imageAssets.clientLogos.pawStuffs, link: 'https://pawstuffs.com', projectType: 'real-world' as const, animationDirection: 'right' as const },
    { title: 'Cleaning EDGE', category: t.portfolio.catBusinessWebsite, descKey: 'cleaningEdge' as const, image: imageAssets.portfolio.cleaningEdge, link: 'https://cleaningedge.ca', projectType: 'real-world' as const, animationDirection: 'left' as const, caseStudyId: 'cleaning-edge-platform' },
    { title: 'Cheese Panic', category: t.portfolio.catWebGame, descKey: 'cheesePanic' as const, image: imageAssets.portfolio.cheesepanic, link: 'https://cheesepanic.impresstars.com', projectType: 'showcase' as const, animationDirection: 'left' as const },
    { title: 'Cleaning EDGE App', category: t.portfolio.catWebApp, descKey: 'cleaningEdgeApp' as const, image: imageAssets.portfolio.cleaningEdge, link: 'https://ceapp.cleaningedge.ca', projectType: 'showcase' as const, animationDirection: 'right' as const },
    { title: 'Angolo Diamonds', category: t.portfolio.catWebsite, descKey: 'angoloDiamonds' as const, image: imageAssets.portfolio.angoloDiamonds, link: 'https://angolodiamonds.com', projectType: 'real-world' as const, animationDirection: 'left' as const },
    { title: 'Craft Papi Growth Plan', category: t.portfolio.catConsultingMarketingPlan, descKey: 'craftPapiGrowth' as const, image: imageAssets.portfolio.craftPapi, link: 'https://craftpapi.com', projectType: 'real-world' as const, animationDirection: 'right' as const },
    { title: 'Robotic Voice Clicker', category: t.portfolio.catInteractiveApp, descKey: 'roboticVoiceClicker' as const, image: imageAssets.portfolio.roboticVoiceClicker, link: 'https://jorgelizard.impresstars.com', projectType: 'showcase' as const, animationDirection: 'right' as const },
    { title: 'Cheat Counter', category: t.portfolio.catUtilityTool, descKey: 'cheatCounter' as const, image: imageAssets.portfolio.cheatCounter, link: 'https://cheatcounter.bolt.host', projectType: 'showcase' as const, animationDirection: 'left' as const },
    { title: 'Klondike Solitaire', category: t.portfolio.catGameExperience, descKey: 'klondikeSolitaire' as const, image: imageAssets.portfolio.klondikeSolitaire, link: 'https://klondike.impresstars.com', projectType: 'showcase' as const, animationDirection: 'right' as const },
    { title: 'Universe Adventure', category: t.portfolio.catGameExperience, descKey: 'universeAdventure' as const, image: imageAssets.portfolio.universeAdventure, link: 'https://universeadventure.impresstars.com', projectType: 'showcase' as const, animationDirection: 'left' as const },
    { title: '3D Interior Design', category: t.portfolio.cat3DWebSandbox, descKey: 'interiorDesign3d' as const, image: imageAssets.portfolio.interiorDesign3d, link: 'https://vocal-fairy-723927.netlify.app', projectType: 'showcase' as const, animationDirection: 'left' as const },
    { title: 'Pavillion Jewellery', category: t.portfolio.catJewelleryBrand, descKey: 'pavillionJewellery' as const, image: imageAssets.portfolio.pavilionJewellery, link: 'https://impresstars.com/portfolio/pavillion-jewellery', projectType: 'real-world' as const, animationDirection: 'left' as const },
    { title: 'Haunted Galleria', category: t.portfolio.catWebDevelopment, descKey: 'hauntedGalleria' as const, image: imageAssets.portfolio.hauntedGalleria, link: 'https://hauntedgalleria.impresstars.com', projectType: 'showcase' as const, animationDirection: 'right' as const },
  ];

  const WEBSITE_CATEGORIES = new Set([t.portfolio.catBrandWebsite, t.portfolio.catWebDevelopment, t.portfolio.catEcommerceWebsite, t.portfolio.catBusinessWebsite, t.portfolio.catWebsite, t.portfolio.catJewelleryBrand]);
  const APP_CATEGORIES = new Set([t.portfolio.catWebApp, t.portfolio.catInteractiveApp, t.portfolio.catUtilityTool, t.portfolio.cat3DWebSandbox]);
  const GAME_CATEGORIES = new Set([t.portfolio.catWebGame, t.portfolio.catGameExperience]);
  const CONSULTING_CATEGORIES = new Set([t.portfolio.catConsultingMarketingPlan]);

  type PortfolioFilter = 'all' | 'websites' | 'apps' | 'games' | 'consulting';
  const [portfolioFilter, setPortfolioFilter] = useState<PortfolioFilter>('all');

  const filterLabels: Record<PortfolioFilter, string> = {
    all: t.portfolio.filterAll,
    websites: t.portfolio.filterWebsites,
    apps: t.portfolio.filterApps,
    games: t.portfolio.filterGames,
    consulting: t.portfolio.filterConsulting,
  };

  return (
    <>
      <SEO
        title={t.seo.homeTitle}
        description={t.seo.homeDescription}
      />
      {/* Hero Section */}
      <section ref={heroRef} className="hero-atmosphere relative text-white py-24 px-4 md:px-[5vw]">
        <HeroFluid />
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-3xl">
            <AnimatedSection animation="fade-in-up">
              <h1 className="layered-title text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="layered-title__echo" aria-hidden="true">{t.hero.title}</span>
                <span className="layered-title__text">{t.hero.title.split(' ').slice(0, -2).join(' ')}
                  <span className="hero-accent text-[#FDCB12]"> {t.hero.title.split(' ').slice(-2).join(' ')}</span>
                </span>
              </h1>
            </AnimatedSection>
            <AnimatedSection animation="fade-in-up" delay={200}>
              <p className="text-lg sm:text-xl mb-8 text-gray-300">
                {t.hero.subtitle}
              </p>
            </AnimatedSection>
            <AnimatedSection animation="fade-in-up" delay={300} className="flex flex-wrap gap-4">
              <Link to={localizedPath('/#services')} className="button-shine bg-[#FDCB12] text-black px-6 py-3 rounded-md hover:bg-[#EA580C] hover:text-black transition-all duration-300 transform hover:-translate-y-0.5 flex items-center">
                {t.hero.exploreServices}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to={localizedPath('/#about')} className="border-2 border-[#FDCB12] text-[#FDCB12] px-6 py-3 rounded-md hover:bg-[#FDCB12] hover:text-black transition-all duration-300">
                {t.hero.learnMore}
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <SectionHeading eyebrow={t.services.eyebrow} title={t.services.title} emphasis />
          <AnimatedSection animation="fade-in-up" delay={200} className="stagger-grid grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <ServiceCard
              icon={<Building2 className="h-8 w-8" />}
              title={t.services.incorporation.title}
              description={t.services.incorporation.description}
              link={localizedPath('/services/incorporation')}
              animationDirection="left"
            />
            <ServiceCard
              icon={<BrainCircuit className="h-8 w-8" />}
              title={t.services.aiSolutions.title}
              description={t.services.aiSolutions.description}
              link={localizedPath('/services/ai-solutions')}
              animationDirection="right"
            />
            <ServiceCard
              icon={<Globe2 className="h-8 w-8" />}
              title={t.services.b2bSolutions.title}
              description={t.services.b2bSolutions.description}
              link={localizedPath('/services/b2b-solutions')}
              animationDirection="left"
            />
            <ServiceCard
              icon={<Megaphone className="h-8 w-8" />}
              title={t.services.marketingStrategy.title}
              description={t.services.marketingStrategy.description}
              link={localizedPath('/services/marketing-strategy')}
            />
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={300} className="stagger-grid grid md:grid-cols-4 gap-8 mb-8">
            <ServiceCard
              icon={<Laptop className="h-8 w-8" />}
              title={t.services.businessSoftware.title}
              description={t.services.businessSoftware.description}
              link={localizedPath('/services/business-software')}
              animationDirection="left"
            />
            <ServiceCard
              icon={<Palette className="h-8 w-8" />}
              title={t.services.brandDesign.title}
              description={t.services.brandDesign.description}
              link={localizedPath('/services/brand-design')}
              animationDirection="right"
            />
            <ServiceCard
              icon={<BarChart3 className="h-8 w-8" />}
              title={t.services.businessConsulting.title}
              description={t.services.businessConsulting.description}
              link={localizedPath('/services/business-consulting')}
              animationDirection="left"
            />
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={400} className="w-full bg-surface-2 p-16 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-zinc-700">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-4xl font-bold mb-6 text-white">{t.services.ctaTitle}</h3>
              <p className="text-xl text-gray-400 mb-8">{t.services.ctaText}</p>
              <Link to={localizedPath('/#contact')} className="inline-block bg-[#FDCB12] text-black px-8 py-4 rounded-md text-lg font-semibold hover:bg-[#EA580C] hover:text-black transition-all duration-300 transform hover:scale-105">
                {t.services.ctaButton}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Star Models Preview */}
      <section id="star-models" className="py-20 bg-base">
        <div className="container mx-auto px-4">
          <SectionHeading eyebrow={t.starModel.eyebrow} title={t.starModel.title} description={t.starModel.description} />
          <AnimatedSection animation="scale-in"><StarSystem /></AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={100} className="text-center mb-8">
            <button
              type="button"
              onClick={() => scrollToSection('star-models')}
              className="button-shine inline-flex items-center bg-[#FDCB12] text-black px-6 py-3 rounded-md hover:bg-[#EA580C] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(253,203,18,.2)]"
            >
              {t.starModel.learnHow}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200} className="stagger-grid grid sm:grid-cols-1 md:grid-cols-2 gap-8">
            <StarModelCard
              title={t.starModel.cleaningEdge.title}
              status={t.starModel.cleaningEdge.status}
              description={t.starModel.cleaningEdge.description}
              details={t.starModel.cleaningEdge.details}
              link="https://cleaningedge.ca"
            />
            <StarModelCard
              title={t.starModel.ozean.title}
              status={t.starModel.ozean.status}
              description={t.starModel.ozean.description}
              details={t.starModel.ozean.details}
              link="https://ozean.ca"
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-surface-2">
        <div className="container mx-auto px-4">
          <SectionHeading eyebrow={t.testimonials.eyebrow} title={t.testimonials.title} description={t.testimonials.description} />
          <AnimatedSection animation="fade-in-up" delay={200} className="stagger-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <TestimonialCard
              company="J94 Designs"
              testimonial={t.testimonials.j94Designs}
            />
            <TestimonialCard
              company="Craft Papi"
              testimonial={t.testimonials.craftPapi}
            />
            <TestimonialCard
              company="Paw Stuffs"
              testimonial={t.testimonials.pawStuffs}
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <SectionHeading eyebrow={t.portfolio.eyebrow} title={t.portfolio.title} description={t.portfolio.description} emphasis />

          {/* Filter Tabs */}
          <AnimatedSection animation="fade-in-up" delay={100}>
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {(Object.keys(filterLabels) as PortfolioFilter[]).map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setPortfolioFilter(filter)}
                  aria-pressed={portfolioFilter === filter}
                  className={`filter-pill px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                    portfolioFilter === filter
                      ? 'bg-[#FDCB12] text-black border-[#FDCB12]'
                      : 'bg-transparent text-gray-400 border-zinc-700 hover:border-zinc-500 hover:text-white'
                  }`}
                >
                  {filterLabels[filter]}
                </button>
              ))}
            </div>
          </AnimatedSection>

          <div key={portfolioFilter} className="filter-grid stagger-grid grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems
              .filter(({ category }) => {
                if (portfolioFilter === 'all') return true;
                if (portfolioFilter === 'websites') return WEBSITE_CATEGORIES.has(category);
                if (portfolioFilter === 'apps') return APP_CATEGORIES.has(category);
                if (portfolioFilter === 'games') return GAME_CATEGORIES.has(category);
                if (portfolioFilter === 'consulting') return CONSULTING_CATEGORIES.has(category);
                return true;
              })
              .map((item) => (
                <PortfolioCard
                  key={item.title}
                  title={item.title}
                  category={item.category}
                  description={t.portfolio.desc[item.descKey]}
                  image={item.image}
                  link={item.link}
                  projectType={item.projectType}
                  animationDirection={item.animationDirection}
                  caseStudyId={item.caseStudyId}
                />
              ))}
          </div>
        </div>
      </section>

      {/* Partners Teaser */}
      <section className="py-16 bg-surface-2">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-in-up">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-10 gap-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-[#FDCB12]">{t.partners.teaserEyebrow}</span>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">{t.partners.teaserTitle}</h2>
              </div>
              <Link
                to={localizedPath('/partners')}
                className="inline-flex items-center gap-2 text-[#FDCB12] font-medium hover:text-[#FF5500] transition-colors group"
              >
                {t.partners.seeAllPartners} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnersData.map((partner) => (
              <PartnerTeaserCard key={partner.name} partner={partner} />
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <SectionHeading eyebrow={t.about.eyebrow} title={t.about.title} />
          <AnimatedSection className="max-w-3xl mx-auto text-gray-300">
            <p className="mb-6">
              {t.about.p1}
            </p>
            <p className="mb-6">
              {t.about.p2}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center text-black">
              {t.clients.title}
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              {t.clients.description}
            </p>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200} className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            {visibleClients.map((client, index) => (
              <ClientCard key={`${client.name}-${index}`} client={client} />
            ))}
          </AnimatedSection>
        </div>
      </section>


      {/* Technologies & Platforms Section */}
      <section id="partners" className="py-20 bg-surface-2">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center text-white">{t.techPlatforms.title}</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
              {t.techPlatforms.description}
            </p>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                category: t.techPlatforms.catEcommerce,
                focus: t.techPlatforms.focusEcommerce,
                tools: ['Shopify', 'Wix', 'WordPress', 'Webflow', 'React', 'TypeScript']
              },
              {
                category: t.techPlatforms.catAI,
                focus: t.techPlatforms.focusAI,
                tools: ['OpenAI API Integrations', 'Prompt Engineering', 'AI Chatbots', 'Workflow Automation', 'ElevenLabs', 'Suno']
              },
              {
                category: t.techPlatforms.catMarketing,
                focus: t.techPlatforms.focusMarketing,
                tools: ['Google Analytics', 'Google Ads', 'Meta Ads', 'SEO Foundations', 'Email Marketing', 'CRM Setup']
              },
              {
                category: t.techPlatforms.catCloud,
                focus: t.techPlatforms.focusCloud,
                tools: ['Node.js', 'Netlify', 'Google Cloud', 'Amazon Web Services (AWS)', 'Microsoft Azure', 'Git']
              },
              {
                category: t.techPlatforms.catCreative,
                focus: t.techPlatforms.focusCreative,
                tools: ['Adobe Photoshop', 'Adobe Lightroom', 'Brand Systems', 'Process Automation', 'Unity', 'Unreal Engine']
              },
            ].map((group, index) => {
              const delay = (((index % 5) + 1) * 100) as 100 | 200 | 300 | 400 | 500;

              return (
                <AnimatedSection key={group.category} animation="fade-in-right" delay={delay}>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 h-full transition-all duration-700 ease-out hover:border-[#FDCB12]">
                    <h3 className="text-lg font-semibold text-white mb-2">{group.category}</h3>
                    <p className="text-sm text-gray-400 mb-4">{group.focus}</p>
                    <div className="flex flex-wrap gap-2">
                      {group.tools.map((tool) => (
                        <span key={tool} className="text-xs bg-zinc-800 text-gray-200 px-2 py-1 rounded">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center text-white">{t.contact.title}</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              {t.contact.description}
            </p>
          </AnimatedSection>
          <AnimatedSection animation="fade-in-up" delay={200}>
            <div className="max-w-xl mx-auto">
              {formStatus === 'success' ? (
                <div className="success-burst bg-green-900/30 border border-green-500 rounded-lg p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-4" aria-hidden="true" />
                  <h3 className="text-2xl font-bold text-green-400 mb-2">{t.contact.successTitle}</h3>
                  <p className="text-gray-300">{t.contact.successText}</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 bg-[#FDCB12] text-black px-6 py-2 rounded-md hover:bg-[#EA580C] hover:text-black transition-all duration-300"
                  >
                    {t.contact.sendAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="hidden" name="form-name" value="main-form" />
                  <div className="hidden">
                    <input name="bot-field" />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">{t.contact.name}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-md text-white focus:outline-none focus:border-[#FDCB12] transition-colors"
                      placeholder={t.contact.namePlaceholder}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">{t.contact.email}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-md text-white focus:outline-none focus:border-[#FDCB12] transition-colors"
                      placeholder={t.contact.emailPlaceholder}
                    />
                  </div>
                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-300 mb-2">{t.contact.reason}</label>
                    <select
                      id="reason"
                      name="reason"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-md text-white focus:outline-none focus:border-[#FDCB12] transition-colors"
                    >
                      <option value="" disabled>{t.contact.reasonPlaceholder}</option>
                      <option value="incorporation-services">{t.contact.reasons.incorporationServices}</option>
                      <option value="ai-solutions">{t.contact.reasons.aiSolutions}</option>
                      <option value="b2b-solutions">{t.contact.reasons.b2bSolutions}</option>
                      <option value="marketing-strategy">{t.contact.reasons.marketingStrategy}</option>
                      <option value="business-software">{t.contact.reasons.businessSoftware}</option>
                      <option value="brand-design">{t.contact.reasons.brandDesign}</option>
                      <option value="business-consulting">{t.contact.reasons.businessConsulting}</option>
                      <option value="website-development">{t.contact.reasons.websiteDevelopment}</option>
                      <option value="website-support">{t.contact.reasons.websiteSupport}</option>
                      <option value="ecommerce-support">{t.contact.reasons.ecommerceSupport}</option>
                      <option value="general-question">{t.contact.reasons.generalQuestion}</option>
                      <option value="partnership">{t.contact.reasons.partnership}</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">{t.contact.message}</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-md text-white focus:outline-none focus:border-[#FDCB12] transition-colors resize-none"
                      placeholder={t.contact.messagePlaceholder}
                    />
                  </div>
                  {RECAPTCHA_KEY && (
                    <div className="flex justify-center">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_KEY}
                        theme="dark"
                        onChange={(value) => setCaptchaValue(value)}
                        onExpired={() => setCaptchaValue(null)}
                      />
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-red-400 text-sm text-center">{errorMessage}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="button-shine w-full bg-[#FDCB12] text-black py-3 rounded-md font-semibold hover:bg-[#EA580C] hover:text-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? t.contact.sending : t.contact.send}
                  </button>
                </form>
              )}
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}

export default HomePage;
